import { Button, Drawer } from "@/components";
import { ArrowLeft, Check, Copy, Download } from "@/components/Icons";
import { Input } from "@/components/Input";
import { Loading } from "@/components/Loading";
import styles from "@/styles/containers/logs.module.scss";
import { IndexedDBStorage } from "@/tools/Logger";
import classNames from "classnames/bind";
import { useEffect, useMemo, useState } from "react";

const css = classNames.bind(styles);

const formatLogsAsText = (logs) =>
  logs
    .map((item) => {
      const header = `[${item.createdAt.toLocaleString()}] [${item.level.toUpperCase()}] [${String(
        item.version,
      ).toUpperCase()}] ${item.message}`;

      if (!item.metadata) {
        return header;
      }

      return `${header}\n${JSON.stringify(item.metadata, null, 2)}`;
    })
    .join("\n\n");

const copyToClipboard = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  // Fallback para WebViews sem suporte à Clipboard API
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  let succeeded = false;
  try {
    succeeded = document.execCommand("copy");
  } catch (err) {
    succeeded = false;
  }

  document.body.removeChild(textarea);
  return succeeded;
};

const downloadAsFile = (text) => {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const fileName = `logs-${new Date().toISOString().replace(/[:.]/g, "-")}.txt`;

  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

const LEVEL_LABELS = {
  ERROR: "Erro",
  WARN: "Aviso",
  INFO: "Info",
  LOG: "Log",
};

export default function Logs({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copyState, setCopyState] = useState("idle");
  const [expandedIndex, setExpandedIndex] = useState(null);

  const [term, setTerm] = useState("");

  const getLogs = async (term) => {
    setLoading(true);

    const storage = new IndexedDBStorage();

    const logs = await storage.getLogs(term);

    setLogs(logs);
    setLoading(false);
  };

  useEffect(() => {
    getLogs();
  }, []);

  useEffect(() => {
    getLogs(term);
  }, [term]);

  const logsAsText = useMemo(() => formatLogsAsText(logs), [logs]);

  const handleCopy = async () => {
    const succeeded = await copyToClipboard(logsAsText);
    setCopyState(succeeded ? "copied" : "error");
    setTimeout(() => setCopyState("idle"), 2000);
  };

  const handleDownload = () => {
    downloadAsFile(logsAsText);
  };

  return (
    <Drawer open={true} onClose={onClose} withoutPadding>
      <div className={styles.logsContainer}>
        <div className={styles.header}>
          <div className={styles.back} onClick={onClose}>
            <ArrowLeft />
          </div>
          <p className={styles.title}>Logs</p>

          <div className={styles.actions}>
            <Button
              design="secondary-two"
              className={styles.actionButton}
              disabled={!logs.length}
              onClick={handleCopy}
            >
              {copyState === "copied" ? (
                <>
                  <Check color="#3d734a" size={16} /> Copiado
                </>
              ) : (
                <>
                  <Copy size={16} /> Copiar
                </>
              )}
            </Button>

            <Button
              design="secondary-two"
              className={styles.actionButton}
              disabled={!logs.length}
              onClick={handleDownload}
            >
              <Download size={16} /> Exportar
            </Button>

            <Button
              design="secondary-two"
              className={styles.actionButton}
              onClick={() => getLogs(term)}
            >
              Atualizar
            </Button>
          </div>
        </div>

        <div className={styles.searchBar}>
          <Input
            design="default"
            className={styles.searchInput}
            type={"text"}
            value={term}
            placeholder={"Pesquisar nos logs..."}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>

        {copyState === "error" && (
          <p className={styles.copyError}>
            Não foi possível copiar automaticamente. Selecione o texto
            manualmente.
          </p>
        )}

        {loading && (
          <div className={styles.loading}>
            <Loading />
          </div>
        )}

        {!loading && !logs.length && (
          <div className={styles.empty}>
            <p className={styles.title}>Não há eventos registrados</p>
          </div>
        )}

        {!loading && logs.length > 0 && (
          <div className={styles.list}>
            {logs.map((item, index) => {
              const isExpanded = expandedIndex === index;
              const hasMetadata = !!item.metadata;

              return (
                <div
                  key={index}
                  className={css({
                    logItem: true,
                    [String(item.level).toLowerCase()]: true,
                  })}
                >
                  <div
                    className={css({
                      logRow: true,
                      clickable: hasMetadata,
                    })}
                    onClick={() =>
                      hasMetadata && setExpandedIndex(isExpanded ? null : index)
                    }
                  >
                    <span className={styles.level}>
                      {LEVEL_LABELS[String(item.level).toUpperCase()] ||
                        item.level}
                    </span>
                    <span className={styles.timestamp}>
                      {item.createdAt.toLocaleString()}
                    </span>
                    <span className={styles.version}>v{item.version}</span>
                    <p className={styles.message}>{item.message}</p>
                    {hasMetadata && (
                      <span className={styles.expandHint}>
                        {isExpanded ? "▲" : "▼"}
                      </span>
                    )}
                  </div>

                  {hasMetadata && isExpanded && (
                    <pre className={styles.metadata}>
                      <code>{JSON.stringify(item.metadata, null, 2)}</code>
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Drawer>
  );
}
